import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { of, catchError, map, switchMap, throwError, exhaustMap, concatMap } from "rxjs";
import { environment } from "src/environments/environment";
import { CCSession } from "../interfaces/user.interface";

@Injectable()
export class CorpService {
    private _CC_API_CONTEXT: string;
    private _AUTH_USER_ACTION: string;
    private _VALIDATE_SID_ACTION: string;
    private _VALIDATE_SID_ROLES_ACTION: string;
    private _CURRENT_SESSION_KEY: string;
    public currentSession: CCSession;
    constructor(
        private http: HttpClient,
    ) {
        this._CC_API_CONTEXT = '/APICorpCommon/AppData/';
        this._AUTH_USER_ACTION = 'AuthenticateUserPost';
        this._VALIDATE_SID_ACTION = 'validateSID';
        this._VALIDATE_SID_ROLES_ACTION = 'validateSIDWithRoles';
        this._CURRENT_SESSION_KEY = 'CURRENT_SESSION';
    }

    public getData = (dataOptions) => {
        // return this.sendDataRequest(dataOptions, 'GetData', 'Get', 'text')
        let getUrl = dataOptions.url || `${environment.MVC_URL}/${environment.MVC_DATA_CONTEXT}/GetData`;
        let ccDataParams = this.buildCCDataDefinition(dataOptions.session, dataOptions.actionKey, dataOptions.data);
        let paramifiedData = this.urlParamifyData(ccDataParams);
        let useCredentials = this.shouldUseCredentials(dataOptions);
        let contentType = dataOptions.contentType ? dataOptions.contentType : 'application/json';
        let url = getUrl + `?${paramifiedData}`;
        let headers: HttpHeaders = new HttpHeaders();
        headers.append('Content-Type', contentType);
        if (dataOptions.sendCorpHeaders) {
            headers.append('corp-un', dataOptions.session?.userId);
            headers.append('corp-auth-token', dataOptions.session?.guid);
        }

        var options = {
            method: 'Get',
            headers: headers,
            url: url,
            withCredentials: useCredentials,
            responseType: 'json' as 'json'
        };
        return this.http.get(url, options).pipe(
            map(
                (res: any) => {
                    return this.processCorpCommonDataResult(res, dataOptions.asString)
                }
            )
        )
    };

    public postForm = (dataOptions) => {
        var updatedOptions = Object.assign({}, dataOptions);
        updatedOptions.contentType = 'application/x-www-form-urlencoded';
        updatedOptions.httpVerb = 'Post';
        return of(this.request(updatedOptions));
    };
    public postData = (dataOptions) => {
        let updatedOptions = Object.assign({}, dataOptions);
        updatedOptions.contentType = 'application/x-www-form-urlencoded';
        updatedOptions.httpVerb = 'Post';
        let url = dataOptions.url || `${environment.MVC_URL}/${environment.MVC_DATA_CONTEXT}/PostData`;
        let useCredentials = this.shouldUseCredentials(dataOptions);
        let contentType = dataOptions.contentType ? dataOptions.contentType : 'application/json';
        let headers: HttpHeaders = new HttpHeaders();
        headers.append('Content-Type', contentType);
        if (dataOptions.sendCorpHeaders) {
            headers.append('corp-un', dataOptions.session?.userId);
            headers.append('corp-auth-token', dataOptions.session?.guid);
        }
        let postData: FormData = new FormData();
        let processDef = JSON.stringify({
            ProcessDataAction: dataOptions.actionKey,
            AppDataDefinition: dataOptions.data
        });
        postData.append('sid', dataOptions.session?.guid);
        postData.append('userId', dataOptions.session?.userId);
        postData.append('applicationId', environment.APPLICATION_ID);
        postData.append('corpProcessDataDefinition', processDef);

        var options = {
            method: 'Post',
            headers: headers,
            url: url,
            withCredentials: useCredentials,
            responseType: 'json' as 'json'
        };
        options['body'] = postData;
        return this.http.post(url, postData, options).pipe(
            map((res: any) => {
                return this.processCorpCommonDataResult(res, false)
            })
        )
        // return this.sendDataRequest(updatedOptions, 'PostData', 'Post', 'text');
    };

    private prepareUrl = (inputUrl) => {
        var urlResult = inputUrl;
        // If the inputUrl is NOT a full url path,
        // append it to the end of the BaseMVCConfig
        if (inputUrl && inputUrl.indexOf('http') < 0) {
            var newUrl = environment.MVC_URL;
            if (newUrl >= '' && inputUrl.indexOf('/') > 0) {
                newUrl += '/';
            }
            urlResult = newUrl + urlResult;
        }
        return urlResult;
    };

    private request = (dataOptions) => {
        let _this = this;
        if (dataOptions === void 0) {
            dataOptions = {
                url: '',
                contentType: 'application/json',
                customHeaders: [],
                httpVerb: 'Get',
            };
        }
        return new Promise(function (resolve, reject) {
            var requestUrl = dataOptions.isRelative ? dataOptions.url : _this.prepareUrl(dataOptions.url);
            var contentType = dataOptions.contentType ? dataOptions.contentType : 'application/json';
            var headers = new HttpHeaders();
            headers.append('Content-Type', contentType);
            _this.addCorpHeadersIfNeeded(dataOptions, headers);
            if (dataOptions.customHeaders !== undefined && dataOptions.customHeaders !== null) {
                dataOptions.customHeaders.forEach(function (h) {
                    headers.append(h.key, h.value);
                });
            }
            var verb = dataOptions.httpVerb;
            // if (dataOptions.httpVerb !== undefined && dataOptions.httpVerb !== null) {
            //     switch (dataOptions.httpVerb) {
            //         case 'DELETE':
            //             verb = RequestMethod.Delete;
            //             break;
            //         case 'PATCH':
            //             verb = RequestMethod.Patch;
            //             break;
            //         case 'Post':
            //             verb = RequestMethod.Post;
            //             break;
            //         case 'PUT':
            //             verb = RequestMethod.Put;
            //             break;
            //     }
            // }
            var url = requestUrl;
            var body = undefined;
            if (dataOptions.data !== undefined && dataOptions.data !== null) {
                if (verb === 'Get' || verb === 'DELETE') {
                    var dataParams = _this.urlParamifyData(dataOptions.data);
                    if (dataParams !== '') {
                        url = requestUrl.concat('?', dataParams);
                    }
                }
                else if (contentType === 'application/x-www-form-urlencoded') {
                    body = _this.urlParamifyData(dataOptions.data);
                }
                else {
                    body = typeof (dataOptions.data) === 'object' ? JSON.stringify(dataOptions.data) : dataOptions.data;
                }
            }
            var options = {
                // method: verb,
                url: url,
                withCredentials: dataOptions.withCredentials || environment.USE_CREDENTIALS,
                headers,
                responseType: dataOptions.responseType || 'arraybuffer'
            };
            if (body !== undefined) {
                options['body'] = body;
            }
            _this.http.request(verb, requestUrl, options)
                .pipe(
                    map(function (res: any) {
                        if (dataOptions.responseType !== undefined && dataOptions.responseType !== null) {
                            switch (dataOptions.responseType) {
                                case 'json':
                                    return this.parseJson(res);
                                case 'text':
                                    return res.text();
                                case 'arraybuffer':
                                    return res.arrayBuffer();
                                case 'blob':
                                    return res.blob();
                            }
                        }
                        else if (dataOptions.asRaw) {
                            return res.text();
                        }
                        else {
                            return this.parseJson(res);
                        }
                    }),
                    // switchMap((data) => of(_this.resolveDataOrRejectError(data, resolve, reject)))
                    // subscribe((data) => { return _this.resolveDataOrRejectError(data, resolve, reject); }, function (err) { return throwError(() => new Error(_this.parseError(err))); })
                )
        })
    };

    private parseError = (error, prefix?) => {
        if (prefix === void 0) { prefix = undefined; }
        var msg = this.extractError(error);
        if (prefix !== undefined) {
            msg.message = prefix.concat(msg.message);
        }
        return msg;
    };

    private extractError = (error) => {
        if (error === undefined) {
            return { message: 'Undefined Error' };
        }
        if (typeof (error) === 'object') {
            if (error.message) {
                return error;
            }
            else if (error._body && error.status) {
                return { message: error.status + '(' + error._body + ')' };
            }
            else {
                return { message: JSON.stringify(error) };
            }
        }
        if (typeof (error) === 'string') {
            return { message: error };
        }
        return { message: 'Unknown, Unparseable Error' };
    };

    private addCorpHeadersIfNeeded = (dataOptions, headers) => {
        if (dataOptions.sendCorpHeaders && dataOptions.session !== undefined && dataOptions.session !== null) {
            headers.append('corp-un', dataOptions.session?.userId);
            headers.append('corp-auth-token', dataOptions.session?.guid);
        }
    };

    private resolveDataOrRejectError = (data, resolve, reject) => {
        if (data === undefined || data.errorExists) {
            return of({
                message: data.message
            })
        }
        else {
            return of(data);
        }
    };
    private sendDataRequest = (dataOptions, routeAction, method, responseType) => {
        let getUrl = dataOptions.url || `${environment.MVC_URL}/${environment.MVC_DATA_CONTEXT}/${routeAction}`;
        let ccDataParams = this.buildCCDataDefinition(dataOptions.session, dataOptions.actionKey, dataOptions.data);
        let paramifiedData = this.urlParamifyData(ccDataParams);
        let useCredentials = this.shouldUseCredentials(dataOptions);
        let contentType = dataOptions.contentType ? dataOptions.contentType : 'application/json';
        let url = getUrl + (method === 'Get' ? `?${paramifiedData}` : '');
        let headers: HttpHeaders = new HttpHeaders();
        headers.append('Content-Type', contentType);
        if (dataOptions.sendCorpHeaders) {
            headers.append('corp-un', dataOptions.session?.userId);
            headers.append('corp-auth-token', dataOptions.session?.guid);
        }

        var options = {
            method: method,
            headers: headers,
            url: url,
            withCredentials: useCredentials,
            responseType: responseType
        };


        if (method === 'Post') {
            options["body"] = paramifiedData;
        }

        return this.http.request(method, url, options).pipe(
            map((res: any) => this.processCorpCommonDataResult(JSON.parse(res), dataOptions.asString)),
            // concatMap((data) => this.processCorpCommonDataResult(data, dataOptions.asString)),
            // concatMap(data => data),
            catchError((error: Error) => throwError(() => this.parseError(error)))
        )
        // .subscribe({
        //     next: (data) => of(this.processCorpCommonDataResult(data, dataOptions.asString)),
        //     error: err => throwError(() => this.parseError(err))
        // })
        // http.request(getUrl, options)
        //     .map(function (res) { return this.parseJson(res); })
        //     .subscribe(function (data) {
        //         return this.processCorpCommonDataResult(data, resolve, reject, dataOptions.asString);
        //     },
        // function (err) { return reject(this.utils.parseError(err, errorPrefix)); });;
    }

    private processCorpCommonDataResult = (result, asString) => {
        //  
        try {

            if (result === undefined || result.errorExists)
                throw result.message;
            else {
                // Convert String result to Object
                var dataResult = result.data;
                if (!asString && dataResult && typeof (dataResult) === 'string') {
                    dataResult = JSON.parse(dataResult);
                }
                return (dataResult);
            }
        } finally { }
    }

    private shouldUseCredentials = (dataOptions) => (dataOptions && dataOptions.withCredentials !== undefined && dataOptions.withCredentials !== null) ?
        dataOptions.withCredentials :
        true;

    private buildCCDataDefinition = (session, actionKey, dataInput) => {
        let processDef = JSON.stringify({
            ProcessDataAction: actionKey,
            AppDataDefinition: dataInput
        });
        let inputData = {
            sid: session?.guid,
            userID: session?.userId,
            applicationID: session?.applicationID,
            corpProcessDataDefinition: processDef
        };
        return inputData;
    }

    private urlParamifyData = (data) => {
        if (typeof (data) !== 'object') {
            throw new Error('Cannot paramify non-object input. Input was of type: ' + typeof (data));
        }
        var str = [];
        Object.keys(data).forEach(function (prop) {
            var name = encodeURIComponent(prop);
            var value = data[prop];
            // Anything complex, we need to JSON.Stringify
            if (typeof (value) === 'object') {
                value = JSON.stringify(value);
            }
            var val = encodeURIComponent(value);
            str.push(name + '=' + val);
        });
        return str.join('&');
    }
}
